import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FaArrowLeft, FaPaperPlane, FaSpinner, FaUser, FaBox, FaImage
} from 'react-icons/fa';

export default function ProductConversation() {
  const { i18n } = useTranslation();
  const { productId, conversationId } = useParams();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [product, setProduct] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, conversationId]);

  useEffect(() => {
    const msgCount = conversation?.messages?.length || 0;
    // Only scroll to bottom on initial load or when new messages are added
    if (isInitialLoad.current && msgCount > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      previousMessageCount.current = msgCount;
    } else if (msgCount > previousMessageCount.current) {
      scrollToBottom();
      previousMessageCount.current = msgCount;
    }
  }, [conversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch product
      const productRes = await axios.get(`${API}/products/${productId}`);
      setProduct(productRes.data);
      
      // Fetch conversation
      if (conversationId) {
        const convRes = await axios.get(`${API}/products/conversations/${conversationId}?user_id=${user.id}`);
        setConversation(convRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API}/products/conversations/${conversationId}/message?user_id=${user.id}&content=${encodeURIComponent(newMessage)}`
      );
      
      setNewMessage('');
      fetchData(); // Refresh conversation
      toast.success('Message envoyé');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isMyMessage = (senderId) => senderId === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#0A2540]" />
      </div>
    );
  }

  const otherPartyName = conversation?.buyer_id === user?.id 
    ? conversation?.seller_name 
    : conversation?.buyer_name;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <FaArrowLeft className="mr-2" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">
              💬 Conversation
            </h1>
            <p className="text-gray-500 text-sm">
              Avec {otherPartyName || 'Utilisateur'}
            </p>
          </div>
        </div>

        {/* Product Info */}
        {product && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {product.images?.[0] ? (
                  <img 
                    src={product.images[0]} 
                    alt={product.name_fr}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <FaBox className="text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <Link to={`/product/${productId}`} className="hover:underline">
                    <h3 className="font-bold text-[#0A2540]">{product.name_fr}</h3>
                  </Link>
                  <div className="flex items-center gap-3 mt-1">
                    {product.price_rental && (
                      <Badge className="bg-[#00D4FF]">{product.price_rental} €/jour</Badge>
                    )}
                    {product.price_new && (
                      <Badge className="bg-[#635BFF]">{product.price_new} €</Badge>
                    )}
                    {product.location && (
                      <span className="text-sm text-gray-500">📍 {product.location}</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FaUser className="text-[#00D4FF]" />
              Messages avec {otherPartyName}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] overflow-y-auto bg-gray-50 rounded-lg p-4 mb-4">
              {conversation?.messages?.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                  Aucun message pour le moment
                </div>
              ) : (
                <div className="space-y-4">
                  {conversation?.messages?.map((msg, idx) => (
                    <div 
                      key={msg.id || idx}
                      className={`flex ${isMyMessage(msg.sender_id) ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[70%] rounded-lg p-3 ${
                          isMyMessage(msg.sender_id) 
                            ? 'bg-[#0A2540] text-white' 
                            : 'bg-white border shadow-sm'
                        }`}
                      >
                        <p className={`text-xs mb-1 ${isMyMessage(msg.sender_id) ? 'text-white/70' : 'text-gray-500'}`}>
                          {msg.sender_name} • {formatTime(msg.created_at)}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((att, i) => (
                              <a 
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-sm ${
                                  isMyMessage(msg.sender_id) ? 'text-white/90' : 'text-[#0A2540]'
                                }`}
                              >
                                <FaImage /> {att.filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="flex gap-3">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                className="flex-1"
                disabled={sending}
              />
              <Button type="submit" disabled={sending || !newMessage.trim()}>
                {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
